import React from 'react';
import styled from 'styled-components';
import { Spinner } from 'react-bootstrap';
import { uiTokens } from './tokens';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconOnly?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const sizeMap: Record<ButtonSize, { h: string; px: string; fs: string }> = {
  sm: { h: '36px', px: '0.85rem', fs: '13px' },
  md: { h: '44px', px: '1.15rem', fs: '15px' },
  lg: { h: '48px', px: '1.4rem', fs: '16px' },
};

const StyledButton = styled.button<{ $variant: ButtonVariant; $size: ButtonSize; $iconOnly: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: ${({ $size }) => sizeMap[$size].h};
  min-width: ${({ $iconOnly }) => ($iconOnly ? '44px' : 'unset')};
  padding: ${({ $iconOnly, $size }) => ($iconOnly ? '0' : `0 ${sizeMap[$size].px}`)};
  border-radius: 12px;
  border: 1px solid transparent;
  font-size: ${({ $size }) => sizeMap[$size].fs};
  font-weight: 600;
  font-family: ${uiTokens.typography.sans};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  ${({ $variant }) => {
    if ($variant === 'secondary') {
      return `
        background: rgba(52, 211, 153, 0.12);
        border-color: rgba(52, 211, 153, 0.35);
        color: ${uiTokens.colors.textPrimary};
      `;
    }
    if ($variant === 'ghost') {
      return `
        background: transparent;
        border-color: rgba(148, 163, 184, 0.25);
        color: ${uiTokens.colors.textPrimary};
      `;
    }
    return `
      background: ${uiTokens.colors.accent};
      color: ${uiTokens.colors.accentTextDark};
    `;
  }}

  &:hover:not(:disabled) {
    background: ${({ $variant }) => ($variant === 'primary' ? uiTokens.colors.accentHover : 'rgba(52, 211, 153, 0.2)')};
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  iconOnly = false,
  startIcon,
  endIcon,
  children,
  disabled,
  ...rest
}) => {
  return (
    <StyledButton
      $variant={variant}
      $size={size}
      $iconOnly={iconOnly}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <Spinner animation="border" size="sm" style={{ width: '1rem', height: '1rem', color: uiTokens.colors.accentTextDark }} />
      ) : (
        <>
          {startIcon}
          {!iconOnly ? children : null}
          {endIcon}
        </>
      )}
    </StyledButton>
  );
};

export default Button;
